import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { fetchAdminOrders, AdminOrderSummary } from './adminService';
import { useAdminSession } from './adminSession';
import { ApiError } from '../../services/apiClient';
import { ORDER_STATUS_LABEL, OrderStatus, WORK_STATUS_LABEL, WorkStatus } from '../../types/order';
import { SHIPPING_METHOD_LABEL } from '../../types/orderPolicy';
import { RequestType } from '../Quote/quoteInquiryService';

const PAGE_TEXT: Record<RequestType, { title: string; listTitle: string; empty: string }> = {
  INQUIRY: { title: '견적문의', listTitle: '견적문의 목록', empty: '접수된 견적문의가 없습니다.' },
  ORDER: { title: '주문관리', listTitle: '주문 목록', empty: '주문이 없습니다.' },
};

// 결제 상태: 결제완료 / 후불(미결제) / 미결제
function paymentLabel(order: AdminOrderSummary): { label: string; tone: 'ok' | 'warn' | 'neutral' } {
  if (order.paid_at) return { label: '결제완료', tone: 'ok' };
  if (order.payment_terms === 'POSTPAID') return { label: '후불 · 미결제', tone: 'warn' };
  return { label: '미결제', tone: 'neutral' };
}


const ORDER_STATUS_BADGE: Record<OrderStatus, 'ok' | 'warn' | 'info' | 'neutral' | 'danger'> = {
  PENDING_QUOTE: 'warn',
  QUOTE_CONFIRMED: 'info',
  PENDING_PAYMENT: 'warn',
  PAYMENT_COMPLETED: 'info',
  PROOF_IN_PROGRESS: 'info',
  PROOF_FEEDBACK: 'warn',
  PROOF_APPROVED: 'ok',
  PRODUCTION: 'ok',
  SHIPPED: 'ok',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
};

const WORK_STATUS_BADGE: Record<WorkStatus, 'ok' | 'warn' | 'info'> = {
  WAITING: 'warn',
  IN_PROGRESS: 'info',
  COMPLETED: 'ok',
};

// 견적문의(/admin/inquiries)와 주문관리(/admin/orders)가 함께 쓰는 목록 화면
function AdminOrders({ requestType }: { requestType: RequestType }) {
  const text = PAGE_TEXT[requestType];
  const isOrder = requestType === 'ORDER';
  const [session, setSession] = useAdminSession();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<AdminOrderSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchAdminOrders(session.token, requestType)
      .then(setOrders)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setSession(null);
        } else {
          setError(err instanceof Error ? err.message : '주문 목록을 불러오지 못했습니다.');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, requestType]);

  return (
    <AdminLayout title={text.title}>
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">{text.listTitle}</div>
          <div className="sec-actions">
            <span className="chip active">전체 {orders?.length ?? 0}건</span>
          </div>
        </div>
        <div className="card" style={{ padding: 0 }}>
          {error && (
            <div className="emptyRow" style={{ color: '#B91C1C' }}>
              {error}
            </div>
          )}
          {!error && (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>주문번호</th>
                    <th>고객명</th>
                    <th>연락처</th>
                    <th>제품 구성</th>
                    <th>수량</th>
                    {isOrder && <th>디자인</th>}
                    <th>배송</th>
                    <th style={{ textAlign: 'right' }}>합계</th>
                    {isOrder && <th>결제</th>}
                    <th>상태</th>
                    {isOrder && <th>작업 상태</th>}
                    <th>접수일</th>
                  </tr>
                </thead>
                <tbody>
                  {orders && orders.length === 0 && (
                    <tr>
                      <td colSpan={isOrder ? 12 : 9} className="emptyRow">
                        {text.empty}
                      </td>
                    </tr>
                  )}
                  {!orders && (
                    <tr>
                      <td colSpan={isOrder ? 12 : 9} className="emptyRow">
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                  {orders?.map((order) => (
                    <tr key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)}>
                      <td>
                        <span className="idcell">{order.order_no}</span>
                      </td>
                      <td>
                        <div className="co">
                          {/* 고객 아이콘: 사이드바 하단 사용자 아이콘(.foot-avatar)과 같은 모양 */}
                          <span className="foot-avatar" aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M4 21a8 8 0 0 1 16 0" />
                            </svg>
                          </span>
                          <div>
                            <strong>{order.customer_name}</strong>
                          </div>
                        </div>
                      </td>
                      <td>{order.phone}</td>
                      <td>{order.shape ?? '-'}</td>
                      <td>{order.quantity.toLocaleString()}</td>
                      {isOrder && (
                        <td>{order.design_type === 'HAVE' ? '있음' : order.design_type === 'NONE' ? '없음' : '-'}</td>
                      )}
                      <td>{SHIPPING_METHOD_LABEL[order.shipping_method] ?? '-'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="price">
                          {order.total_price != null ? `₩ ${order.total_price.toLocaleString()}` : '미정'}
                        </span>
                      </td>
                      {isOrder && (
                        <td>
                          <span className={`badgePill ${paymentLabel(order).tone}`}>{paymentLabel(order).label}</span>
                        </td>
                      )}
                      <td>
                        <span className={`badgePill ${ORDER_STATUS_BADGE[order.status]}`}>
                          <span className="pulse"></span> {ORDER_STATUS_LABEL[order.status]}
                        </span>
                      </td>
                      {isOrder && (
                        <td>
                          <span className={`badgePill ${WORK_STATUS_BADGE[order.work_status]}`}>
                            <span className="pulse"></span> {WORK_STATUS_LABEL[order.work_status]}
                          </span>
                        </td>
                      )}
                      <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminOrders;
